import { useState } from "react";
import { Send, MoreVert, AddCommentOutlined, Check, CheckOutlined, CheckCircleOutline } from "@mui/icons-material";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";

interface CommentData {
  id: string;
  author: string;
  content: string;
  timestamp: string;
  isResolved?: boolean;
}

const mockComments: CommentData[] = [
  {
    id: "1",
    author: "Sarah Chen",
    content: "The payment terms in section 3 need clarification. Should we specify the exact payment schedule?",
    timestamp: "2 hours ago",
  },
  {
    id: "2", 
    author: "Mike Johnson",
    content: "Agreed on the payment terms. Also, can we add a clause about intellectual property rights?",
    timestamp: "1 hour ago",
  },
  {
    id: "3",
    author: "Alex Rivera",
    content: "The deadline seems tight. Can we extend it by 2 weeks?",
    timestamp: "30 minutes ago",
    isResolved: true,
  },
];

export const Comments = () => {
  const [comments, setComments] = useState<CommentData[]>(mockComments);
  const [newComment, setNewComment] = useState("");

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    
    const comment: CommentData = {
      id: Date.now().toString(),
      author: "You",
      content: newComment,
      timestamp: "Just now",
    };
    
    setComments([...comments, comment]);
    setNewComment("");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <AddCommentOutlined className="w-5 h-5 text-white" />
        <h3 className="text-lg font-medium">Comments</h3>
        <span className="text-xs bg-white/10 text-white px-2 py-1 rounded-full">
          {comments.filter(c => !c.isResolved).length}
        </span>
      </div>

      {/* Comments list */}
      <div className="space-y-3 max-h-[300px] overflow-y-auto custom-scrollbar">
        {comments.map((comment) => (
          <div key={comment.id} className={`p-4 rounded-2xl ${
            comment.isResolved 
              ? 'bg-green-500/5' 
              : 'bg-white/20'
          }`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-b from-neutral-500 to-neutral-600 rounded-full flex items-center justify-center">
                  <span className="text-base font-medium text-white">
                    {comment.author.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <span className="text-base font-medium text-white">{comment.author}</span>
                {comment.isResolved && (
                  <span className="text-sm bg-green-500/20 text-green-300 px-2 py-1 rounded-full">
                    Resolved
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button className="text-neutral-400 hover:text-white">
                  <CheckCircleOutline className="w-4 h-4" />
                </button>
                <button className="text-neutral-400 hover:text-white">
                  <MoreVert className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className="text-base text-neutral-300 mb-2">{comment.content}</p>
            <span className="text-xs text-neutral-500">{comment.timestamp}</span>
          </div>
        ))}
      </div>

      {/* Add comment */}
      <div className="space-y-2">
        <Input
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Add a comment..."
          className="bg-white/5 border-white/10 text-white placeholder:text-neutral-400"
          multiple={false}
        />
      </div>
    </div>
  );
};
